/**
 * 001-catalogue-admin-screens T062 (discount-rules-wizard). Same repeatable-row pattern
 * `components/sku-unit-editor.tsx` and `components/orders/delivery-zone-editor.tsx` already port
 * from vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/product/create-edit/shipping-info.tsx
 * — `useFieldArray`, a labelled control per column, a per-row `ActionIcon` + trash icon, a bottom
 * "Add" button — with reordering (`move`) added, because here the array's own position IS the
 * pricing decision (FR-022): `sequence` is derived from a row's index, never typed by hand, so a
 * duplicate `sequence` (one of the API's three 422 causes, pricing-admin.md) is structurally
 * impossible, the same intent `isPrimary`'s radio group states in the sibling editor.
 *
 * The outer shell (`formCard`/`formSections`/`FormFooter`) is the same one `pages/tiers/New.tsx`
 * and `pages/tiers/Detail.tsx` use — this screen is the third form under `/tiers`, so it gets the
 * same card, the same section divider, and the same Cancel+Submit bar rather than a bespoke footer.
 * "Cancel" navigates away (`onCancel`, supplied by the page) the same as every other tier form's
 * Cancel — there is no standalone "back" link on this page for it to defer to.
 *
 * FR-027 (`discountPct` 0–100) and "`scopeId` required unless scope is `all`" (the other two 422
 * causes) are mirrored as inline, submit-time checks — the API's own 422 `detail` is what actually
 * decides (data-model.md's own rule about client-side mirrors) and is rendered verbatim via the
 * `error` prop, exactly as `SkuUnitEditor`'s `error` does.
 */
import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { PiArrowDownBold as ArrowDown, PiArrowUpBold as ArrowUp, PiListChecksDuotone as RulesIcon, PiPlusBold as Plus, PiTrashDuotone as Trash2 } from "react-icons/pi";
import { ActionIcon, Button, Input } from "rizzui";
import FormFooter from "@core/components/form-footer";
import { FormGroup } from "@steelyard/theme";
import { formCard, formCardFooter, formSections } from "../lib/form-layout";
import { SectionTitle } from "./section-title";
import { ScopePicker } from "./scope-picker";
import type { Brand, Category, DiscountRuleInput, DiscountRuleView, DiscountScope } from "../lib/services/catalog";

type RuleRow = {
  scope: DiscountScope;
  scopeId: string | null;
  discountPct: string;
  /** Carried along so a reordered or freshly-loaded broken row keeps its own warning (data-model §3). */
  scopeResolved: boolean;
  scopeName: string | null;
  scopeIssue: string | null;
};

type FormValues = { rules: RuleRow[] };

function toRows(initialRules: readonly DiscountRuleView[]): RuleRow[] {
  return [...initialRules]
    .sort((a, b) => a.sequence - b.sequence)
    .map((r) => ({
      scope: r.scope,
      scopeId: r.scopeId,
      discountPct: String(r.discountPct),
      scopeResolved: r.scopeResolved,
      scopeName: r.scopeName,
      scopeIssue: r.scopeIssue,
    }));
}

/** No rules yet and a starting discount given: one "All products" row at that %, unsaved until Save. */
function withStarter(rows: RuleRow[], starterPct: number | undefined): RuleRow[] {
  if (rows.length > 0 || !starterPct) return rows;
  return [{ scope: "all", scopeId: null, discountPct: String(starterPct), scopeResolved: true, scopeName: null, scopeIssue: null }];
}

const NEW_ROW: RuleRow = { scope: "all", scopeId: null, discountPct: "0", scopeResolved: true, scopeName: null, scopeIssue: null };

export function DiscountRuleBuilder({
  initialRules,
  brands,
  categories,
  onSave,
  onCancel,
  busy,
  error,
  starterPct,
}: {
  initialRules: readonly DiscountRuleView[];
  /** A just-created tier's starting discount, offered as an unsaved "All products" rule when it has none. */
  starterPct?: number;
  brands: readonly Brand[];
  categories: readonly Category[];
  onSave: (rules: DiscountRuleInput[]) => void;
  onCancel: () => void;
  busy: boolean;
  /** The API's own 422 rule name, verbatim (mirrors `SkuUnitEditor`'s own `error` prop). */
  error?: string;
}) {
  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { rules: withStarter(toRows(initialRules), starterPct) } });
  const { fields, append, remove, move } = useFieldArray({ control, name: "rules" });

  // Resyncs after a save — the server now holds exactly this ordered set (same reasoning as
  // SkuUnitEditor's own resync effect).
  useEffect(() => {
    // The starter too: this also runs on mount, and would otherwise wipe it straight away.
    reset({ rules: withStarter(toRows(initialRules), starterPct) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRules]);

  const watchedRows = watch("rules");

  const submit = handleSubmit((values) => {
    clearErrors();
    let hasError = false;
    const out: DiscountRuleInput[] = [];

    values.rules.forEach((row, index) => {
      const discountPct = Number(row.discountPct);
      if (!row.discountPct.trim() || !Number.isFinite(discountPct) || discountPct < 0 || discountPct > 100) {
        setError(`rules.${index}.discountPct`, { message: "Must be between 0 and 100" });
        hasError = true;
      }
      if (row.scope !== "all" && !row.scopeId) {
        // Surfaced inline via `needsTarget` below (computed from live `watch` state, not
        // `errors`) — nothing to set here beyond blocking the submit.
        hasError = true;
      }
      out.push({ sequence: index + 1, scope: row.scope, scopeId: row.scope === "all" ? null : row.scopeId, discountPct });
    });

    if (hasError) return;
    onSave(out);
  });

  const brandName = new Map(brands.map((b) => [b.id, b.name]));
  const categoryName = new Map(categories.map((c) => [c.id, c.name]));

  /** A rule as a sentence — what it takes off, and from what. Names only; no price is worked out here. */
  function describe(row: RuleRow | undefined): string {
    if (!row) return "";
    const pct = row.discountPct.trim() === "" ? "…" : `${row.discountPct}%`;
    const target = row.scopeId ? ((row.scope === "brand" ? brandName.get(row.scopeId) : categoryName.get(row.scopeId)) ?? row.scopeName) : null;
    switch (row.scope) {
      case "all":
        return `${pct} off every product this tier sells.`;
      case "brand":
        return target ? `${pct} off every product of the brand ${target}.` : `${pct} off one brand — choose which.`;
      case "category":
        return target ? `${pct} off everything in ${target}, including its sub-categories.` : `${pct} off one category — choose which.`;
      case "subcategory":
        return target ? `${pct} off everything in ${target}.` : `${pct} off one sub-category — choose which.`;
      case "sku":
        return target ? `${pct} off the SKU ${target} only.` : `${pct} off one SKU only.`;
    }
  }

  return (
    <form onSubmit={submit} className={formCard}>
      {error ? (
        <p role="alert" className="mb-4 text-sm text-red-dark">
          {error}
        </p>
      ) : null}

      <div className={formSections}>
        <FormGroup
          title={<SectionTitle icon={RulesIcon}>Discount rules</SectionTitle>}
          description="Each rule takes a percentage off the base price. Order matters only when two equally specific rules cover the same product — the higher one wins."
        >
          <div className="col-span-full flex flex-col gap-3">
            {fields.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-muted px-6 py-6 text-center">
                <RulesIcon aria-hidden className="size-6 text-muted-foreground" />
                <p className="text-sm font-semibold text-foreground">No rules yet</p>
                <p className="max-w-sm text-sm text-muted-foreground">
                  With no rules, every product on this tier sells at its base price. Start with an &ldquo;All products&rdquo; rule for the
                  tier&apos;s general discount.
                </p>
                <Button type="button" variant="outline" size="sm" className="mt-1" onClick={() => append({ ...NEW_ROW })}>
                  <Plus aria-hidden className="me-2 size-4" strokeWidth={2} />
                  Add rule
                </Button>
              </div>
            ) : null}

            {fields.map((item, index) => {
              const row = watchedRows[index];
              const needsTarget = row?.scope !== "all" && !row?.scopeId;
              return (
                <div key={item.id} className="rounded-lg border border-muted p-4">
                  <div className="flex flex-wrap items-start gap-3">
                    <span
                      className="mt-7 flex size-8 shrink-0 items-center justify-center rounded-full bg-primary-lighter text-sm font-semibold text-primary-dark"
                      title={`Priority ${index + 1} — used only to break a tie between equally specific rules`}
                    >
                      {index + 1}
                    </span>

                    <div className="min-w-0 flex-1">
                      <ScopePicker
                        idPrefix={`rule-${index}`}
                        scope={row?.scope ?? "all"}
                        scopeId={row?.scopeId ?? null}
                        brands={brands}
                        categories={categories}
                        onScopeChange={(scope) => setValue(`rules.${index}.scope`, scope)}
                        onScopeIdChange={(scopeId) => setValue(`rules.${index}.scopeId`, scopeId)}
                        unresolved={row && !row.scopeResolved ? { scopeName: row.scopeName, scopeIssue: row.scopeIssue } : undefined}
                        targetError={needsTarget ? "Choose a target" : undefined}
                      />
                    </div>

                    <Input
                      label="Discount %"
                      inputMode="decimal"
                      className="w-32 shrink-0"
                      inputClassName="font-mono"
                      {...register(`rules.${index}.discountPct`)}
                      error={errors.rules?.[index]?.discountPct?.message}
                    />

                    <div className="mt-7 flex shrink-0 gap-1">
                      <ActionIcon type="button" variant="flat" disabled={index === 0} onClick={() => move(index, index - 1)} aria-label={`Move rule ${index + 1} up`}>
                        <ArrowUp aria-hidden className="size-4" />
                      </ActionIcon>
                      <ActionIcon
                        type="button"
                        variant="flat"
                        disabled={index === fields.length - 1}
                        onClick={() => move(index, index + 1)}
                        aria-label={`Move rule ${index + 1} down`}
                      >
                        <ArrowDown aria-hidden className="size-4" />
                      </ActionIcon>
                      <ActionIcon type="button" variant="flat" onClick={() => remove(index)} aria-label={`Remove rule ${index + 1}`}>
                        <Trash2 aria-hidden className="size-4" />
                      </ActionIcon>
                    </div>
                  </div>
                  <p className="ms-11 mt-2 text-[13px] text-gray-600">{describe(row)}</p>
                </div>
              );
            })}

            {fields.length > 0 ? (
              <Button type="button" variant="outline" className="self-start" onClick={() => append({ ...NEW_ROW })}>
                <Plus aria-hidden className="me-2 size-4" strokeWidth={2} />
                Add rule
              </Button>
            ) : null}
          </div>
        </FormGroup>
      </div>

      <FormFooter
        className={formCardFooter}
        submitBtnText={busy ? "Saving…" : "Save rules"}
        isLoading={busy}
        altBtnText="Cancel"
        handleAltBtn={onCancel}
      />
    </form>
  );
}
